import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Collapse,
  IconButton,
  Divider
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Work as WorkIcon,
  Folder as FolderIcon,
  AccessTime as TimeIcon,
  ExpandMore as ExpandMoreIcon,
  OpenInNew as OpenInNewIcon,
  Attachment as AttachmentIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface ProjectDashboardProps {
  project: any;
  dailyReports: any[];
  regieReports: any[];
  documents: any[];
  totalHours: number;
  isLoading: boolean;
  onTabChange?: (tabIndex: number) => void;
}

const ProjectDashboard: React.FC<ProjectDashboardProps> = ({
  project,
  dailyReports,
  regieReports,
  documents,
  totalHours,
  isLoading,
  onTabChange
}) => {
  const navigate = useNavigate();
  const [expandedDailyReports, setExpandedDailyReports] = useState(false);
  const [expandedRegieReports, setExpandedRegieReports] = useState(false);
  const [expandedDocuments, setExpandedDocuments] = useState(false);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const getRemainingDays = () => {
    if (!project.end_date) return 'Kein Enddatum';
    const endDate = new Date(project.end_date);
    const today = new Date();
    const diffTime = endDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Abgeschlossen';
    if (diffDays === 0) return 'Endet heute';
    if (diffDays === 1) return 'Endet morgen';
    return `${diffDays} Tage verbleibend`;
  };

  const getLastReportDate = (reports: any[]) => {
    if (reports.length === 0) return 'Keine Berichte';
    
    const lastReport = reports[reports.length - 1];
    const lastDate = new Date(lastReport.date);
    const today = new Date();
    const diffTime = today.getTime() - lastDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Heute';
    if (diffDays === 1) return 'Gestern';
    if (diffDays < 7) return `vor ${diffDays} Tagen`;
    if (diffDays < 30) return `vor ${Math.floor(diffDays / 7)} Wochen`;
    return `vor ${Math.floor(diffDays / 30)} Monaten`;
  };

  const getTotalFileSize = () => {
    if (!documents || documents.length === 0) return '0 MB';
    
    const totalBytes = documents.reduce((sum: number, doc: any) => sum + (doc.file_size || 0), 0);
    const totalMB = Math.round(totalBytes / (1024 * 1024));
    return `${totalMB} MB`;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Projekt-Übersicht */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom color="primary">
            Projekt-Übersicht
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="h5" fontWeight="bold">
                {project.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {project.description || 'Keine Beschreibung verfügbar'}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip 
                label={project.status || 'Aktiv'} 
                color={project.status === 'Abgeschlossen' ? 'success' : 'primary'}
                size="small"
              />
              <Chip 
                label={getRemainingDays()} 
                color={getRemainingDays().includes('verbleibend') ? 'warning' : 'default'}
                size="small"
              />
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body2">
                <strong>Kunde:</strong> {project.client_name || 'Nicht angegeben'}
              </Typography>
              <Typography variant="body2">
                <strong>Adresse:</strong> {project.site_address || 'Nicht angegeben'}
              </Typography>
              {project.start_date && (
                <Typography variant="body2">
                  <strong>Start:</strong> {new Date(project.start_date).toLocaleDateString('de-DE')}
                </Typography>
              )}
              {project.end_date && (
                <Typography variant="body2">
                  <strong>Ende:</strong> {new Date(project.end_date).toLocaleDateString('de-DE')}
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Berichte & Dokumente */}
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {/* Tagesberichte */}
        <Card sx={{ flex: 1, minWidth: 250 }}>
          <CardContent>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                mb: 2,
                cursor: 'pointer'
              }}
              onClick={() => setExpandedDailyReports(!expandedDailyReports)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DescriptionIcon color="primary" />
                <Typography variant="h6">Tagesberichte</Typography>
              </Box>
              <IconButton 
                size="small"
                sx={{
                  transform: expandedDailyReports ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s'
                }}
              >
                <ExpandMoreIcon />
              </IconButton>
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="h4" color="primary">
                {dailyReports.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Letzter: {getLastReportDate(dailyReports)}
              </Typography>
            </Box>

            <Collapse in={expandedDailyReports} timeout="auto" unmountOnExit>
              <Divider sx={{ my: 2 }} />
              {dailyReports.length > 0 ? (
                <List dense>
                  {dailyReports.map((report: any) => (
                    <ListItem 
                      key={report.id}
                      sx={{ 
                        px: 0,
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <ListItemIcon>
                        <DescriptionIcon fontSize="small" color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`TB #${report.rapport_nr || report.id}`}
                        secondary={new Date(report.date).toLocaleDateString('de-DE')}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<OpenInNewIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/tagesberichte/${report.id}`);
                        }}
                      >
                        Öffnen
                      </Button>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  Keine Tagesberichte vorhanden
                </Typography>
              )}
            </Collapse>
          </CardContent>
        </Card>

        {/* Regieberichte */}
        <Card sx={{ flex: 1, minWidth: 250 }}>
          <CardContent>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                mb: 2,
                cursor: 'pointer'
              }}
              onClick={() => setExpandedRegieReports(!expandedRegieReports)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WorkIcon color="secondary" />
                <Typography variant="h6">Regieberichte</Typography>
              </Box>
              <IconButton 
                size="small"
                sx={{
                  transform: expandedRegieReports ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s'
                }}
              >
                <ExpandMoreIcon />
              </IconButton>
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="h4" color="secondary">
                {regieReports.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Letzter: {getLastReportDate(regieReports)}
              </Typography>
            </Box>

            <Collapse in={expandedRegieReports} timeout="auto" unmountOnExit>
              <Divider sx={{ my: 2 }} />
              {regieReports.length > 0 ? (
                <List dense>
                  {regieReports.map((report: any) => (
                    <ListItem 
                      key={report.id}
                      sx={{ 
                        px: 0,
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <ListItemIcon>
                        <WorkIcon fontSize="small" color="secondary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={`RB #${report.rapport_nr || report.id}`}
                        secondary={new Date(report.date).toLocaleDateString('de-DE')}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<OpenInNewIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/regie-reports/${report.id}`);
                        }}
                      >
                        Öffnen
                      </Button>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  Keine Regieberichte vorhanden
                </Typography>
              )}
            </Collapse>
          </CardContent>
        </Card>

        {/* Dokumente */}
        <Card sx={{ flex: 1, minWidth: 250 }}>
          <CardContent>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                mb: 2,
                cursor: 'pointer'
              }}
              onClick={() => setExpandedDocuments(!expandedDocuments)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FolderIcon color="info" />
                <Typography variant="h6">Dokumente</Typography>
              </Box>
              <IconButton 
                size="small"
                sx={{
                  transform: expandedDocuments ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s'
                }}
              >
                <ExpandMoreIcon />
              </IconButton>
            </Box>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="h4" color="info.main">
                {documents.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Gesamtgröße: {getTotalFileSize()}
              </Typography>
            </Box>

            <Collapse in={expandedDocuments} timeout="auto" unmountOnExit>
              <Divider sx={{ my: 2 }} />
              {documents.length > 0 ? (
                <List dense>
                  {documents.map((doc: any) => (
                    <ListItem 
                      key={doc.id}
                      sx={{ 
                        px: 0,
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <ListItemIcon>
                        <AttachmentIcon fontSize="small" color="info" />
                      </ListItemIcon>
                      <ListItemText
                        primary={doc.name || doc.file_name}
                        secondary={`${((doc.file_size || 0) / 1024).toFixed(0)} KB`}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<OpenInNewIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          // Dokumente in neuem Tab öffnen
                          window.open(doc.file_path || doc.url, '_blank');
                        }}
                      >
                        Öffnen
                      </Button>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  Keine Dokumente vorhanden
                </Typography>
              )}
            </Collapse>
          </CardContent>
        </Card>
      </Box>

      {/* Arbeitszeit & Gesamtstunden */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <TimeIcon color="success" />
            <Typography variant="h6">Arbeitszeit</Typography>
          </Box>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h4" color="success.main">
                {(totalHours || 0).toFixed(1)}h
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Gesamtstunden für dieses Projekt
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Chip 
                label={`${dailyReports.length} Tagesberichte`} 
                icon={<DescriptionIcon />} 
                size="small"
                onClick={() => onTabChange?.(2)}
                clickable
                sx={{ cursor: 'pointer' }}
              />
              <Chip 
                label={`${regieReports.length} Regieberichte`} 
                icon={<WorkIcon />} 
                size="small"
                onClick={() => onTabChange?.(3)}
                clickable
                sx={{ cursor: 'pointer' }}
              />
              <Chip 
                label={`${documents.length} Dokumente`} 
                icon={<FolderIcon />} 
                size="small"
                onClick={() => onTabChange?.(1)}
                clickable
                sx={{ cursor: 'pointer' }}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProjectDashboard;
